import React, { useEffect, useState } from 'react';
import Flex from 'components/common/Flex';
import { useAppContext } from 'providers/AppProvider';
import { Card, Button, ButtonGroup, Spinner } from 'react-bootstrap';
import styled from 'styled-components';
import { anomaliesRank } from 'hooks/CallApi';
import {
  formatCountStyle1,
  formatCountStyle3
} from 'components/common/SunnyUtils';
import { getCompanyColor, getCompanyColorRgba } from 'helpers/companyColors';

const SiteIssueRank = () => {
  const {
    config: {
      dashboardDate,
      dashboardDateValue,
      Chart_startdate,
      Chart_enddate,
      isDemo
    }
  } = useAppContext();

  const [view, setView] = useState('cf'); // 'cf', 'api', 'sdk'
  const [isLoading, setIsLoading] = useState(true);
  const [rankData, setRankData] = useState({
    failCompanies: [],
    idleKeys: []
  });

  const [dateRange, setDateRange] = useState({
    start: Chart_startdate,
    end: Chart_enddate
  });
  useEffect(() => {
    // Chart_startdate 또는 Chart_enddate가 변경될 때 상태 업데이트
    setDateRange({
      start: Chart_startdate,
      end: Chart_enddate
    });
  }, [Chart_startdate, Chart_enddate]);

  useEffect(() => {
    let cancelled = false;
    const fetchRankData = async () => {
      setIsLoading(true);
      try {
        const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await anomaliesRank({
          period: dashboardDateValue, // 기간 설정 (day, week, month)
          start: dateRange.start,
          end: dateRange.end,
          timeZone: clientTimeZone,
          type: view, // 'cf', 'api', 'sdk'
          demo: isDemo
        });
        if (cancelled) return;

        // 데이터 가공
        setRankData({
          failCompanies: [
            {
              name: response.data.top1FailCompany,
              value: response.data.top1FailPer || 0,
              dataSize: response.data.top1FailDataSize || 0 // 추가된 필드
            },
            {
              name: response.data.top2FailCompany,
              value: response.data.top2FailPer || 0,
              dataSize: response.data.top2FailDataSize || 0 // 추가된 필드
            },
            {
              name: response.data.top3FailCompany,
              value: response.data.top3FailPer || 0,
              dataSize: response.data.top3FailDataSize || 0 // 추가된 필드
            }
          ],

          idleKeys: [
            {
              name: response.data.top1NotUsePoliComp,
              value: response.data.top1NotUseCnt || 0
            },
            {
              name: response.data.top2NotUsePoliComp,
              value: response.data.top2NotUseCnt || 0
            },
            {
              name: response.data.top3NotUsePoliComp,
              value: response.data.top3NotUseCnt || 0
            }
            // ].filter((item) => !(item.name === null && item.value === 0)), // 이름이 null이고 값이 0인 항목만 제외
          ]
        });
      } catch (error) {
        if (cancelled) return;
        console.error('고객사 이상 순위 데이터 조회 실패:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchRankData();
    return () => {
      cancelled = true;
    };
  }, [view, dashboardDateValue, dateRange, isDemo]);

  // Max 값을 계산하여 진행바 너비에 활용
  const maxFail = Math.max(...rankData.failCompanies.map(c => Number(c.value) || 0), 1);
  const maxIdle = Math.max(...rankData.idleKeys.map(c => Number(c.value) || 0), 1);

  return (
    <Card className="h-md-100" style={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: 'none', backgroundColor: '#FFFFFF' }}>
      <Card.Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: 0,
          borderBottom: 'none',
          backgroundColor: 'transparent',
          padding: '20px 24px'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <h5 style={{ margin: 0, fontWeight: 700, color: '#111827' }}>고객사 이슈 상위</h5>
          <span
            style={{
              marginLeft: '8px',
              marginTop: 0,
              marginBottom: 0,
              padding: '2px 8px',
              // backgroundColor: '#A1C4E3',
              backgroundColor: '#2C7BE5',
              color: 'white',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            {dashboardDate}
          </span>
        </div>
        <ButtonGroup>
          <Button
            style={{ fontSize: '0.7rem' }}
            variant={view === 'cf' ? 'primary' : 'outline-primary'}
            onClick={() => setView('cf')}
          >
            CF
          </Button>
          <Button
            style={{ fontSize: '0.7rem' }}
            variant={view === 'api' ? 'primary' : 'outline-primary'}
            onClick={() => setView('api')}
          >
            API
          </Button>
          <Button
            style={{ fontSize: '0.7rem' }}
            variant={view === 'sdk' ? 'primary' : 'outline-primary'}
            onClick={() => setView('sdk')}
          // disabled
          >
            SDK
          </Button>
        </ButtonGroup>
      </Card.Header>
      <Card.Body alignItems="center" justifyContent="space-between">
        {isLoading ? (
          <div
            style={{
              width: '100%',
              height: '15rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <RateTop3Section>
            {/* 실패 처리량 섹션 */}
            <GridSection>
              <h6 style={{ fontWeight: 700, color: '#1E3A8A', marginBottom: '8px' }}>실패 처리량</h6>
              {rankData.failCompanies.some(c => c.name) ? (
                rankData.failCompanies.map((company, index) => {
                  const widthPercent = Math.min((company.value / maxFail) * 100, 100);
                  return (
                    <Cell
                      key={index}
                      style={{
                        visibility: company.name ? 'visible' : 'hidden',
                        position: 'relative',
                        border: 'none',
                        background: '#F8F9FA',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${widthPercent}%`, background: getCompanyColorRgba(company.name, 0.2), borderRadius: '8px', zIndex: 1, transition: 'width 0.5s ease-in-out' }}></div>

                      <div style={{ position: 'relative', zIndex: 2, display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: '600', color: '#2C3E50', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company.name}</div>{' '}
                        <div style={{ fontWeight: 'bold', color: getCompanyColor(company.name) }}>
                          {formatCountStyle3(company.dataSize)} 
                          <span style={{ fontSize: '0.85em', opacity: 0.8 }}> ({company.value}%)</span>
                        </div>
                      </div>
                    </Cell>
                )})
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '10rem', color: '#999', fontSize: '0.9rem' }}>
                  조회된 데이터가 없습니다
                </div>
              )}
            </GridSection>

            {/* 유휴 암호키 수 섹션 */}
            <GridSection>
              <h6 style={{ fontWeight: 700, color: '#1E3A8A', marginBottom: '8px' }}>유휴 암호키 수</h6>
              {rankData.idleKeys.some(c => c.name) ? (
                rankData.idleKeys.map((company, index) => {
                  const widthPercent = Math.min((company.value / maxIdle) * 100, 100);
                  return (
                    <Cell
                      key={index}
                      style={{
                        visibility: company.name ? 'visible' : 'hidden',
                        position: 'relative',
                        border: 'none',
                        background: '#F8F9FA',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${widthPercent}%`, background: getCompanyColorRgba(company.name, 0.2), borderRadius: '8px', zIndex: 1, transition: 'width 0.5s ease-in-out' }}></div>

                      <div style={{ position: 'relative', zIndex: 2, display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: '600', color: '#2C3E50', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company.name}</div>{' '}
                        <div style={{ fontWeight: 'bold', color: getCompanyColor(company.name) }}>
                          {company.value}개
                        </div>
                      </div>
                    </Cell>
                )})
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '10rem', color: '#999', fontSize: '0.9rem' }}>
                  조회된 데이터가 없습니다
                </div>
              )}
            </GridSection>
          </RateTop3Section>
        )}
      </Card.Body>
    </Card>
  );
};

const RateTop3Section = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GridSection = styled.div`
  display: grid;
  gap: 10px; /* 행 간 간격 */
  font-size: 16px;
  align-items: start; /* 데이터를 위로 정렬 */
  height: 100%; /* 모든 섹션 높이를 동일하게 유지 */
  min-width: 0;
`;

const Cell = styled.div`
  display: flex;
  flex-direction: row; /* 가로 배치 */
  justify-content: space-between; /* 양 끝 정렬 */
  align-items: center; /* 수직 정렬 */
  border: 1px solid #ddd;
  padding: 10px;
  border-radius: 5px;
  min-height: 60px; /* 다른 셀과 동일한 최소 높이 */
  min-width: 0;

  /* @media (max-width: 1600px) {
    flex-direction: column;
    justify-content: center;
    align-items: start;
    min-height: 80px;
    gap: 0px;
  } */
`;

export default SiteIssueRank;
